//@flow
import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import getter from 'dibs-object-getter';
import Tag from 'dibs-icons/exports/Tag';

import styles from './PhotoDetails.scss';

import type { PhotoDetails_tradePhoto as TradePhoto } from './__generated__/PhotoDetails_tradePhoto.graphql';
import type { PhotoDetails_photoQuery as TradePhotosQueryOutput } from './__generated__/PhotoDetails_photoQuery.graphql';

type TagLinkType = 'style' | 'room';

type TagLinkProps = {
    name: string,
    type: 'style' | 'room',
    urlLabel: string,
};
export const TagLink = (props: TagLinkProps) => {
    const { name, type, urlLabel } = props;
    if (!urlLabel || !name) {
        return null;
    }

    return (
        <a href={`/photos/${type}/${urlLabel}/`} className={styles.tagText}>
            {name}
        </a>
    );
};

type TagType = {
    +name: ?string,
    +urlLabel: ?string,
};
const getTagLinkFromTag = (tag: ?TagType, type: TagLinkType) => {
    if (tag && tag.name && tag.urlLabel) {
        return <TagLink name={tag.name} type={type} urlLabel={tag.urlLabel} />;
    }

    return null;
};

type PhotoDetailsProps = {
    tradePhoto: ?TradePhoto,
    photoQuery: ?TradePhotosQueryOutput,
};

export function PhotoDetailsComponent({
    styleTag,
    roomTag,
    photoName,
    projectUrl,
    projectName,
}: {|
    styleTag: ?TagType,
    roomTag: ?TagType,
    photoName: ?string,
    projectUrl: ?string,
    projectName: ?string,
|}) {
    const styleTagElement = getTagLinkFromTag(styleTag, 'style');
    const roomTagElement = getTagLinkFromTag(roomTag, 'room');

    let tagLinks = null;
    if (styleTagElement || roomTagElement) {
        tagLinks = (
            <div>
                <Tag className={styles.tagIcon} />
                <span className={styles.tagTextWrapper}>
                    {styleTagElement}
                    {styleTagElement && roomTagElement ? ', ' : null}
                    {roomTagElement}
                </span>
            </div>
        );
    }
    return (
        <div className={styles.wrapper}>
            <div className={styles.title}>{photoName}</div>
            <div className={styles.projectLink}>
                {projectUrl ? <a href={projectUrl}>{projectName}</a> : <div>{projectName}</div>}
            </div>
            {tagLinks}
        </div>
    );
}

export const PhotoDetails = (props: PhotoDetailsProps) => {
    const getPhoto = getter(props).at('tradePhoto');
    const getPhotoQuery = getter(props).at('photoQuery');

    const photoName = getPhoto.value('name') || getPhoto.at('room').value('name');

    const projectName = getPhotoQuery.value('projectName');
    const projectUrl = getPhotoQuery.value('projectUrl');

    const styleTag = getPhoto.at('styles').value(0);
    const roomTag = getPhoto.value('room');

    return (
        <PhotoDetailsComponent
            styleTag={styleTag}
            roomTag={roomTag}
            photoName={photoName}
            projectUrl={projectUrl}
            projectName={projectName}
        />
    );
};

export default createFragmentContainer(PhotoDetails, {
    photoQuery: graphql`
        fragment PhotoDetails_photoQuery on TradePhotosQueryOutput {
            projectName
            projectUrl
        }
    `,
    tradePhoto: graphql`
        fragment PhotoDetails_tradePhoto on TradePhoto {
            name
            caption
            styles {
                name
                urlLabel
            }
            room {
                name
                urlLabel
            }
        }
    `,
});
